/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { styled, SxProp, useSx } from 'dripsy';
import { observer, useLocalObservable } from 'mobx-react-lite';
import {
  Video,
  AVPlaybackStatus,
  VideoProps,
  ResizeMode,
} from 'expo-av';
import { View, Platform } from 'react-native';
import { runOnUI, useSharedValue } from 'react-native-reanimated';
import { runInAction } from 'mobx';
import { PortalProvider } from '@gorhom/portal';

import {
  IS_WEB,
  addBrowserFullScreenListener,
  exitBrowserFullScreen,
  requestBrowserFullScreen,
  isBrowserInFullScreen,
  IS_MOBILE,
} from '@utils';
import {
  useDebouncedCallback,
  useThrottledCallback,
  useTimeout,
} from '@hooks';
import { LanguageCodesEnum } from '@constants';
import { Touchable, TouchableTypeEnum } from '../../common/Touchable';
import { SubtitlesType } from './SubtitlesText';
import { VideoPlayerControl } from './VideoPlayerControl';
import { Subtitles } from './Subtitles';
import { VideoPlayerLoader } from './VideoPlayerLoader';
import { TimestampType } from './TimestampText';
import { useVideoPlayerSettings } from './hooks/useVideoPlayerSettings';
import { VideoPlayerSupportComponent } from './VideoPlayerSupportComponent';
import { VideoPlayerRewindArrows } from './VideoPlayerRewindArrows';
import { useVideoPlayerRewind } from './hooks/useVideoPlayerRewind';
import { createAspectRatioStyles } from '@styles';
import {
  useVideoPlayerStream,
  IVideoPlayerStreamOptions,
} from './hooks/useVideoPlayerStream';

const wrapperStyle: SxProp = {
  flex: 1,
};

const Container = styled(View)({
  flex: 1,
  bg: '$black',
});

const TouchableContainer = styled(Touchable)({
  flex: 1,
  justifyContent: 'center',
});

const videoStyle: SxProp = {
  ...createAspectRatioStyles(16 / 9, '100%'),
};

export interface IVideoPlayerStore {
  isPlaying: boolean;
  isVisibleControl: boolean;
  isHovered: boolean;
  isBuffering: boolean;
  isFinished: boolean;
  isShouldPlay: boolean;
  timeoutId: null | ReturnType<typeof setTimeout>;
  isInFullScreen: boolean;
  isVisibleSubtitlesMenu: boolean;
  isVisiblePlaybackRateMenu: boolean;
  isLoaded: boolean;

  setIsPlaying(value: boolean): void;
  switchIsVisibleControl(): void;
  setIsVisibleControl(value: boolean): void;
  setIsHovered(value: boolean): void;
  setIsBuffering(value: boolean): void;
  setIsFinished(value: boolean): void;
  setIsShouldPlay(value: boolean): void;
  setIsInFullScreen(value: boolean): void;
  setIsVisibleSubtitlesMenu(value: boolean): void;
  setIsVisiblePlaybackRateMenu(value: boolean): void;
  setIsLoaded(value: boolean): void;
  moveMouse(): void;

  get isLoading(): boolean;
  get isShowControl(): boolean;
}

export type PlayerSubtitlesType = Record<
  LanguageCodesEnum,
  SubtitlesType | null
>;

export interface IVideoPlayerProps
  extends VideoProps,
    Pick<IVideoPlayerStreamOptions, 'onRefreshTokens'> {
  hasNextVideo?: boolean;
  hasPreviousVideo?: boolean;
  onNextVideo?(): void;
  onPreviousVideo?(): void;
  onPlayStart?(): void;
  onToggleFullScreen?(status: AVPlaybackStatus): void;
  isInFullScreen?: boolean;
  subtitles?: PlayerSubtitlesType;
  onVideoFinish?(): void;
  timestamps?: TimestampType;
  onPressTimestamp?(): void;
  hasSubtitles?: boolean;
  onSwitchSubtitles?(value: LanguageCodesEnum | null): void;
}

const noop = () => {};

interface IFullScreenPortalProviderProps {
  children: React.ReactNode;
  store: IVideoPlayerStore;
}

// Because the SubtitlesMenu component uses the Portal, this creates problems with its display,
// because the Portal requires a Provider, and when the player is in the full-screen mode,
// it is rendered on top of this provider, which causes problems with displaying the SubtitlesMenu in the full-screen mode.
// We are adding another Provider into the full-screen container
function FullScreenPortalProvider({
  children,
  store,
}: IFullScreenPortalProviderProps) {
  return store.isInFullScreen && IS_WEB ? (
    <PortalProvider>{children}</PortalProvider>
  ) : (
    <Fragment>{children}</Fragment>
  );
}

export const VideoPlayer = observer(
  forwardRef<Video, IVideoPlayerProps>(
    (
      {
        hasNextVideo = false,
        hasPreviousVideo = false,
        onNextVideo = noop,
        onPreviousVideo = noop,
        onToggleFullScreen = noop,
        onSwitchSubtitles = noop,
        onPlayStart = noop,
        subtitles,
        isInFullScreen,
        onVideoFinish = noop,
        timestamps = [],
        onPressTimestamp = noop,
        onPlaybackStatusUpdate = noop,
        hasSubtitles = true,
        source,
        onRefreshTokens,
        onError,
        onLoad,
        ...props
      },
      ref,
    ) => {
      const videoPlayerSettings = useVideoPlayerSettings();
      const sx = useSx();
      const progress = useSharedValue(0);
      const cache = useSharedValue(0);
      const min = useSharedValue(0);
      const max = useSharedValue(0);
      const volume = useSharedValue(1);
      const isScrubbing = useSharedValue(false);
      const store = useLocalObservable<IVideoPlayerStore>(() => ({
        isPlaying: false,
        isVisibleControl: true,
        isHovered: false,
        isBuffering: false,
        isFinished: false,
        isShouldPlay: false,
        timeoutId: null,
        isInFullScreen: false,
        isVisibleSubtitlesMenu: false,
        isVisiblePlaybackRateMenu: false,
        isLoaded: false,

        setIsLoaded(value: boolean) {
          this.isLoaded = value;
        },

        setIsPlaying(value: boolean) {
          this.isPlaying = value;
        },

        switchIsVisibleControl() {
          this.isVisibleControl = !this.isVisibleControl;
        },

        setIsVisibleControl(value: boolean) {
          this.isVisibleControl = value;
        },

        setIsHovered(value: boolean) {
          this.isHovered = value;
        },

        setIsBuffering(value: boolean) {
          this.isBuffering = value;
        },

        setIsFinished(value: boolean) {
          this.isFinished = value;
        },

        setIsShouldPlay(value: boolean) {
          this.isShouldPlay = value;
        },

        setIsInFullScreen(value: boolean) {
          this.isInFullScreen = value;
        },

        setIsVisibleSubtitlesMenu(value: boolean) {
          this.isVisibleSubtitlesMenu = value;
        },

        setIsVisiblePlaybackRateMenu(value: boolean) {
          this.isVisiblePlaybackRateMenu = value;
        },

        moveMouse() {
          this.isVisibleControl = true;

          if (this.timeoutId) {
            clearTimeout(this.timeoutId);
          }

          this.timeoutId = setTimeout(() => {
            runInAction(() => {
              this.isVisibleControl = false;
            });
          }, 3000);
        },

        get isShowControl() {
          return (
            this.isVisibleControl ||
            (IS_WEB && !this.isPlaying) ||
            this.isHovered ||
            this.isVisibleSubtitlesMenu ||
            this.isVisiblePlaybackRateMenu
          );
        },

        get isLoading() {
          return (
            (this.isShouldPlay &&
              this.isBuffering &&
              !this.isPlaying) ||
            !this.isLoaded
          );
        },
      }));

      const videoRef = useRef<Video>(null);
      const wrapperRef = useRef<HTMLDivElement>(null);
      const rewind = useVideoPlayerRewind(videoRef);
      const stream = useVideoPlayerStream(videoRef, {
        source,
        onRefreshTokens,
      });

      const timeout = useTimeout(
        () => store.setIsVisibleControl(false),
        3000,
      );

      useImperativeHandle(ref, () => videoRef.current!);

      useEffect(() => {
        if (IS_WEB) {
          addBrowserFullScreenListener(() =>
            store.setIsInFullScreen(isBrowserInFullScreen()),
          );

          document.addEventListener('keydown', (event) => {
            switch (event.code) {
              case 'Space':
                // Prevent scrolling lists when a user presses "Space"
                event.preventDefault();

                togglePlay();
                break;

              case 'ArrowLeft':
                rewind.rewindBackward();
                break;
              case 'ArrowRight':
                rewind.rewindForward();
                break;

              default:
                break;
            }
          });
        }
      }, []);

      useEffect(() => {
        if (typeof isInFullScreen === 'boolean') {
          store.setIsInFullScreen(isInFullScreen);
        }
      }, [isInFullScreen]);

      useEffect(() => {
        if (store.isPlaying && store.isVisibleControl) {
          timeout.startOrRestart();
        }
        if (!store.isPlaying) {
          timeout.clear();
        }
      }, [store.isPlaying]);

      useEffect(() => {
        onSwitchSubtitles(videoPlayerSettings.subtitlesLanguage);
      }, [videoPlayerSettings.subtitlesLanguage]);

      useEffect(() => {
        if (store.isFinished) {
          debouncedVideoFinish();
        }
      }, [store.isFinished]);

      function pressOnVideo() {
        if (IS_WEB) {
          togglePlay();
        } else {
          store.switchIsVisibleControl();
          if (store.isVisibleControl && store.isPlaying) {
            timeout.startOrRestart();
          }
        }
      }

      // function interact() {
      //   if (IS_WEB || (store.isVisibleControl && !store.isPlaying)) {
      //     return;
      //   }
      // }

      function hoverStart() {
        if (store.isInFullScreen) {
          return;
        }

        store.setIsHovered(true);
      }

      function hoverEnd() {
        store.setIsHovered(false);
      }

      const throttledMoveMouse = useThrottledCallback(
        () => {
          if (store.isInFullScreen) {
            store.moveMouse();
          }
        },
        [store.isInFullScreen],
        300,
      );

      // To prevent bug with GestureDetector on web (https://github.com/software-mansion/react-native-gesture-handler/issues/2056)
      // we need to delay invoking onVideoFinish
      const debouncedVideoFinish = useDebouncedCallback(
        () => {
          onVideoFinish?.();
        },
        [hasNextVideo],
        300,
      );

      async function togglePlay() {
        const status = await videoRef.current?.getStatusAsync();

        if (status?.isLoaded && status.isPlaying) {
          videoRef.current?.pauseAsync();
          timeout.clear();
        } else {
          if (store.isFinished) {
            videoRef.current?.playFromPositionAsync(0);
          } else {
            videoRef.current?.playAsync();
          }
          onPlayStart();
        }
      }

      function onSlidingComplete(value: number) {
        if (store.isPlaying) {
          timeout.startOrRestart();
        }

        runOnUI(() => {
          'worklet';

          progress.value = value;
          isScrubbing.value = false;
        })();

        // sometimes the video downloads slowly and we can't get the video duration in time, so we need to check if the correct value is available
        // when we try to set an incorrect value for a video that has not been uploaded we get an error and crash
        if (!Number.isNaN(value)) {
          videoRef.current?.setPositionAsync(value);
        }
      }

      function onSlidingStart() {
        timeout.clear();
        runOnUI(() => {
          'worklet';
          isScrubbing.value = true;
        })();
      }

      function onPlaybackStatusUpdateInner(status: AVPlaybackStatus) {
        onPlaybackStatusUpdate?.(status);

        if (status.isLoaded) {
          // On WEB isLoaded is always true, to check if video is loaded we can use
          // durationMillis, when it is NaN this means that video is not loaded
          if (IS_WEB && Number.isNaN(status.durationMillis)) {
            return;
          }
          store.setIsLoaded(true);
          store.setIsPlaying(status.isPlaying);
          store.setIsBuffering(status.isBuffering);
          store.setIsFinished(
            Platform.select({
              android:
                // TODO: try to find beter solution
                // only Android behaviour
                // For some reason when video loading slowly 'durationMillis' and 'positionMillis' has the same value '1'. If it's happening system set isFinished by 'true' but its incorrect
                status.durationMillis === status.positionMillis &&
                status.durationMillis !== 1,
              default:
                status.durationMillis === status.positionMillis,
            }),
          );
          store.setIsShouldPlay(status.shouldPlay);

          runOnUI(() => {
            'worklet';

            if (status.durationMillis) {
              max.value = status.durationMillis;
            }

            if (status.playableDurationMillis) {
              cache.value = status.playableDurationMillis;
            }

            volume.value = status.volume;

            progress.value = status.positionMillis;
          })();
        }
      }

      async function toggleFullScreen() {
        if (IS_WEB) {
          if (isBrowserInFullScreen()) {
            exitBrowserFullScreen();
          } else {
            requestBrowserFullScreen(wrapperRef.current);
          }
        } else {
          const status = await videoRef.current!.getStatusAsync();

          onToggleFullScreen(status);
        }
      }

      function onErrorInner(error: string) {
        onError?.(error);
        stream.loadFailed(error);
      }

      function onLoadInner(status: AVPlaybackStatus) {
        onLoad?.(status);

        // shouldPlay: true doesn't trigger autoplay on iOS/Android
        // This workaround forces autoplay to work on mobile devices
        if (IS_MOBILE && props.shouldPlay) {
          videoRef.current?.playAsync();
        }
      }

      return (
        // @ts-ignore
        <View ref={wrapperRef} style={sx(wrapperStyle)}>
          {/* We use this component for performance optimization */}
          <VideoPlayerSupportComponent
            videoRef={videoRef}
            store={store}
          />
          <Container
            // This is web props which are not typed in react-native
            // @ts-ignore
            onMouseEnter={hoverStart}
            onMouseOver={hoverStart}
            onMouseLeave={hoverEnd}
            onMouseMove={throttledMoveMouse}
            // onTouchStart={interact}
            // onTouchEnd={interact}
            // onTouchMove={interact}
          >
            <TouchableContainer
              onPress={pressOnVideo}
              type={TouchableTypeEnum.WithoutFeedback}
            >
              <Video
                ref={videoRef}
                style={sx(videoStyle)}
                onPlaybackStatusUpdate={onPlaybackStatusUpdateInner}
                resizeMode={ResizeMode.CONTAIN}
                shouldCorrectPitch
                onError={onErrorInner}
                onLoad={onLoadInner}
                {...props}
              />
            </TouchableContainer>

            <FullScreenPortalProvider store={store}>
              <VideoPlayerControl
                progress={progress}
                max={max}
                min={min}
                cache={cache}
                volume={volume}
                hasNextVideo={hasNextVideo}
                hasPreviousVideo={hasPreviousVideo}
                onNextVideo={onNextVideo}
                onPreviousVideo={onPreviousVideo}
                onSlidingComplete={onSlidingComplete}
                onSlidingStart={onSlidingStart}
                onToggleFullScreen={toggleFullScreen}
                onTogglePlay={togglePlay}
                store={store}
                timestamps={timestamps}
                isScrubbing={isScrubbing}
                onPressTimestamp={onPressTimestamp}
                hasSubtitles={hasSubtitles}
              />
            </FullScreenPortalProvider>

            <VideoPlayerLoader store={store} />

            <VideoPlayerRewindArrows rewind={rewind} />

            <Subtitles
              store={store}
              subtitles={subtitles}
              progress={progress}
            />
          </Container>
        </View>
      );
    },
  ),
);
